import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../auth';
import { SecureVideoPlayer } from '../../components/SecureVideoPlayer';
import { NavBar } from '../../components/NavBar';
import { SharedFooter } from '../../components/SharedFooter';
import { ReferencePackCard, getLicenseLabel as getLicenseLabelShared } from './ReferencePackCard';
import { getCaptureDownloadFilename } from './referencePackDownloadFilenames';
import { getReferencePackThumbnailUrl } from './referencePackImages';
import { authHeaders, jsonAuthHeaders, signInMessageForAuthStatus } from '../../shared/authHeaders';
import { useCopy } from '../../shared/copy/CopyProvider';
import { formatMoneyCompact } from '../../shared/money';
import {
  EMPTY_SOURCE_RECORDING_UPLOAD_PROGRESS,
  getQueuedSourceRecordingProgress,
  sourceRecordingProgressChanged,
  type SourceRecordingUploadProgress,
  uploadQueuedSourceRecordings,
} from '../video/SourceRecordingUploadQueue';
import {
  EMPTY_REFERENCE_PACK_ASSET_UPLOAD_PROGRESS,
  getQueuedReferencePackAssetProgress,
  type ReferencePackAssetUploadProgress,
  uploadQueuedReferencePackAssets,
} from '../video/ReferencePackAssetUploadQueue';

type CaptureStatus = 'undecided' | 'selected' | 'rejected';
// 'unpublished' pauses new purchases (buyers keep access); 'withdrawn' is an
// admin takedown the creator cannot change from the editor.
type ProductStatus = 'draft' | 'ready' | 'unpublished' | 'withdrawn';
type ProductVisibility = 'attendees_only' | 'anyone';
type BuilderTab = 'settings' | 'access' | 'recording' | 'photos';
type AccessDurationUnit = 'days' | 'weeks' | 'months' | 'years';

interface UploadEstimate {
  bytesPerSecond: number | null;
  secondsRemaining: number | null;
}

interface PoseCaptureGroup {
  key: string;
  label: string;
  captures: ReferencePackAsset[];
}

interface CameraCaptureGroup {
  key: string;
  label: string;
  captures: ReferencePackAsset[];
}

interface ReferencePackAsset {
  id: string;
  imageUrl: string;
  thumbnailUrl?: string | null;
  capturedAt: string;
  width: number | null;
  height: number | null;
  format: string | null;
  fileSizeBytes: number | null;
  poseName: string | null;
  poseIndex: number | null;
  cameraId: string | null;
  cameraLabel: string | null;
  curationStatus: CaptureStatus;
}

interface CaptureProduct {
  id: string;
  title: string;
  description: string | null;
  attendeePriceCents: number | null;
  publicPriceCents?: number | null;
  priceCents: number | null;
  license: string | null;
  productVisibility: ProductVisibility;
  showOnProfile: boolean;
  status: ProductStatus;
  coverImageUrl: string | null;
  accessExpiresOn: string | null;
  accessDurationCount: number | null;
  accessDurationUnit: AccessDurationUnit | null;
  captureIds: string[];
  recordingIds: string[];
  requiresBuyerApproval: boolean;
  updatedAt: string;
}

interface ModelRecording {
  id: string;
  label: string | null;
  recordingKind?: 'source' | 'backup';
  status: 'pending' | 'recording' | 'processing' | 'completed' | 'failed';
  hasVideo: boolean;
  contentType: string | null;
  fileSizeBytes: number | null;
  durationSeconds: number | null;
  releaseStatus: 'pending_review' | 'released' | 'rejected';
  priceCents: number | null;
  currency: string;
  startedAt: string | null;
  createdAt: string;
}

interface ReviewData {
  room: {
    code: string;
    name: string;
    scheduledAt: string | null;
  };
  captures: ReferencePackAsset[];
  recordings: ModelRecording[];
  products: CaptureProduct[];
}

interface ProductDraft {
  title: string;
  description: string;
  attendeePrice: string;
  publicPrice: string;
  license: string;
  productVisibility: ProductVisibility;
  showOnProfile: boolean;
  status: ProductStatus;
  requiresBuyerApproval: boolean;
  accessDurationCount: string;
  accessDurationUnit: AccessDurationUnit;
  coverImageUrl: string | null;
  captureIds: string[];
  recordingIds: string[];
}

function formatDate(dateStr: string | null) {
  if (!dateStr) return 'Unscheduled';
  return new Date(dateStr).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatFileSize(bytes: number | null) {
  if (!bytes) return '';
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function triggerBrowserDownload(url: string, filename: string, targetBlank = false) {
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  if (targetBlank) link.target = '_blank';
  link.rel = 'noopener noreferrer';
  document.body.appendChild(link);
  link.click();
  link.remove();
}

function getRecordingDisplayName(recording: ModelRecording) {
  const label = recording.label?.trim();
  if (!label) return 'Session recording';

  const cameraMatch = label.match(/^(.*)\s+-\s+(Camera\s+[A-Z]+)$/i);
  if (cameraMatch) {
    return `${cameraMatch[1]} (${cameraMatch[2]})`;
  }

  return label;
}

function formatPrice(value: number) {
  if (value <= 0) return 'Free';
  return formatMoneyCompact(Math.round(value * 100), 'USD');
}

function getLicenseLabel(license: string): string {
  return getLicenseLabelShared(license) ?? license;
}

function pluralize(count: number, singular: string, plural = `${singular}s`) {
  return `${count} ${count === 1 ? singular : plural}`;
}

const DEFAULT_ACCESS_DURATION_COUNT = '2';
const DEFAULT_ACCESS_DURATION_UNIT: AccessDurationUnit = 'months';

function getAccessDurationLabel(count: string | number | null | undefined, unit: AccessDurationUnit | null | undefined) {
  const numericCount = Number(count);
  if (!Number.isFinite(numericCount) || numericCount <= 0 || !unit) return null;
  const singularUnit = unit.replace(/s$/, '');
  return `${numericCount} ${numericCount === 1 ? singularUnit : unit}`;
}

function getCapturePoseLabel(capture: ReferencePackAsset) {
  const poseName = capture.poseName?.trim();
  if (poseName) return poseName;
  if (capture.poseIndex !== null && capture.poseIndex !== undefined) {
    return `Pose ${capture.poseIndex + 1}`;
  }
  return 'Unlabeled pose';
}

function getCapturePoseKey(capture: ReferencePackAsset) {
  const label = getCapturePoseLabel(capture);
  return `${capture.poseIndex ?? 'unlabeled'}:${label}`;
}

function getCaptureCameraLabel(capture: ReferencePackAsset) {
  return capture.cameraLabel?.trim() || 'Unlabeled camera';
}

function getCaptureCameraKey(capture: ReferencePackAsset) {
  const cameraId = capture.cameraId?.trim();
  return cameraId || `unlabeled:${getCaptureCameraLabel(capture)}`;
}

function getCanonicalCameraLetter(index: number) {
  let label = '';
  let value = Math.max(0, index);

  do {
    label = String.fromCharCode(65 + (value % 26)) + label;
    value = Math.floor(value / 26) - 1;
  } while (value >= 0);

  return label;
}

function getCanonicalCameraLabel(index: number) {
  return `Camera ${getCanonicalCameraLetter(index)}`;
}

function buildCameraCaptureGroups(captures: ReferencePackAsset[]): CameraCaptureGroup[] {
  const groups = new Map<string, CameraCaptureGroup>();

  for (const capture of captures) {
    const key = getCaptureCameraKey(capture);
    const existing = groups.get(key);

    if (existing) {
      existing.captures.push(capture);
    } else {
      groups.set(key, {
        key,
        label: getCaptureCameraLabel(capture),
        captures: [capture],
      });
    }
  }

  const rawGroups = Array.from(groups.values());
  const labelCounts = rawGroups.reduce((counts, group) => {
    counts.set(group.label, (counts.get(group.label) ?? 0) + 1);
    return counts;
  }, new Map<string, number>());
  const hasAmbiguousLabels = rawGroups.some((group) => (
    group.label === 'Unlabeled camera'
    || (labelCounts.get(group.label) ?? 0) > 1
  ));

  if (!hasAmbiguousLabels) return rawGroups;

  return rawGroups.map((group, index) => ({
    ...group,
    label: getCanonicalCameraLabel(index),
  }));
}

function getReviewMediaUploadBytes(data: ReviewData | null) {
  if (!data) return 0;

  const photoBytes = data.captures.reduce((total, capture) => total + (capture.fileSizeBytes ?? 0), 0);
  const videoBytes = data.recordings.reduce((total, recording) => total + (recording.fileSizeBytes ?? 0), 0);
  return photoBytes + videoBytes;
}

function formatTransferRate(bytesPerSecond: number | null) {
  if (!bytesPerSecond || bytesPerSecond <= 0) return null;
  return `${formatFileSize(bytesPerSecond)}/s`;
}

function formatEta(seconds: number | null) {
  if (seconds === null || !Number.isFinite(seconds) || seconds < 0) return null;
  if (seconds < 10) return 'under 10 sec';
  if (seconds < 60) return `${Math.ceil(seconds)} sec`;

  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.ceil(seconds % 60);
  if (minutes < 60) {
    return remainingSeconds > 0 ? `${minutes} min ${remainingSeconds} sec` : `${minutes} min`;
  }

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return remainingMinutes > 0 ? `${hours} hr ${remainingMinutes} min` : `${hours} hr`;
}

function getDefaultProductTitle(roomName: string) {
  const cleanedName = roomName.replace(/\s+/g, ' ').trim();
  return cleanedName || 'Untitled';
}

function getImageDimensions(file: File): Promise<{ width: number | null; height: number | null }> {
  return new Promise((resolve) => {
    const image = new Image();
    const objectUrl = URL.createObjectURL(file);

    image.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve({ width: image.naturalWidth || null, height: image.naturalHeight || null });
    };

    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      resolve({ width: null, height: null });
    };

    image.src = objectUrl;
  });
}

function isImageUpload(file: File) {
  return file.type.startsWith('image/') || /\.(jpe?g|png|webp|avif)$/i.test(file.name);
}

function isVideoUpload(file: File) {
  return file.type.startsWith('video/') || /\.(mp4|webm|mov)$/i.test(file.name);
}

function sortedIds(ids: Iterable<string>) {
  return Array.from(ids).sort();
}

function normalizePriceInput(value: string) {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return value.trim();
  return numericValue.toFixed(2);
}

function areStringArraysEqual(first: string[], second: string[]) {
  if (first.length !== second.length) return false;
  return first.every((value, index) => value === second[index]);
}

export function ReferencePackPage() {
  const { code, roomCode } = useParams<{ code?: string; roomCode?: string }>();
  const sessionCode = code || roomCode;
  const [searchParams] = useSearchParams();
  const requestedProductId = searchParams.get('product');
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const [data, setData] = useState<ReviewData | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [selectedRecordingIds, setSelectedRecordingIds] = useState<Set<string>>(new Set());
  const [activeTab, setActiveTab] = useState<BuilderTab>('settings');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [attendeePrice, setAttendeePrice] = useState('0');
  const [publicPrice, setPublicPrice] = useState('10');
  const [license, setLicense] = useState('classroom_reference');
  const [productVisibility, setProductVisibility] = useState<ProductVisibility>('anyone');
  const [showOnProfile, setShowOnProfile] = useState(false);
  const [status, setStatus] = useState<ProductStatus>('draft');
  const [requiresBuyerApproval, setRequiresBuyerApproval] = useState(false);
  const [accessDurationCount, setAccessDurationCount] = useState(DEFAULT_ACCESS_DURATION_COUNT);
  const [accessDurationUnit, setAccessDurationUnit] = useState<AccessDurationUnit>(DEFAULT_ACCESS_DURATION_UNIT);
  const [coverImageUrl, setCoverImageUrl] = useState<string | null>(null);
  const [coverImageFile, setCoverImageFile] = useState<File | null>(null);
  const [coverImagePreviewUrl, setCoverImagePreviewUrl] = useState<string | null>(null);
  const [editingProductId, setEditingProductId] = useState<string | null>(null);
  const [lastSavedDraft, setLastSavedDraft] = useState<ProductDraft | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activePoseKey, setActivePoseKey] = useState('all');
  const [lightboxCaptureId, setLightboxCaptureId] = useState<string | null>(null);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const [uploadingVideos, setUploadingVideos] = useState(false);
  const [downloadingCaptureId, setDownloadingCaptureId] = useState<string | null>(null);
  const [downloadingRecordingId, setDownloadingRecordingId] = useState<string | null>(null);
  const [deletingRecordingId, setDeletingRecordingId] = useState<string | null>(null);
  const [deletingProduct, setDeletingProduct] = useState(false);
  const [sourceRecordingProgress, setSourceRecordingProgress] = useState<SourceRecordingUploadProgress>(EMPTY_SOURCE_RECORDING_UPLOAD_PROGRESS);
  const [referencePackAssetProgress, setReferencePackAssetProgress] = useState<ReferencePackAssetUploadProgress>(EMPTY_REFERENCE_PACK_ASSET_UPLOAD_PROGRESS);
  const [sessionMediaUploading, setSessionMediaUploading] = useState(false);
  const [sessionMediaUploadError, setSessionMediaUploadError] = useState<string | null>(null);
  const [sessionMediaUploadTotalBytes, setSessionMediaUploadTotalBytes] = useState(0);
  const [sessionMediaUploadEstimate, setSessionMediaUploadEstimate] = useState<UploadEstimate>({
    bytesPerSecond: null,
    secondsRemaining: null,
  });
  const sessionMediaUploadInProgressRef = useRef(false);
  const sessionMediaUploadTotalBytesRef = useRef(0);
  const sessionMediaUploadSamplesRef = useRef<Array<{ at: number; uploadedBytes: number }>>([]);
  const [activeCameraKey, setActiveCameraKey] = useState('all');
  const [shareLinkCopied, setShareLinkCopied] = useState(false);

  const setSessionMediaUploadTotalBytesValue = useCallback((bytes: number) => {
    const nextBytes = Math.max(0, bytes);
    sessionMediaUploadTotalBytesRef.current = nextBytes;
    setSessionMediaUploadTotalBytes(nextBytes);
  }, []);

  const updateSessionMediaUploadTotal = useCallback((
    sourceProgress: SourceRecordingUploadProgress,
    assetProgress: ReferencePackAssetUploadProgress,
    reviewMediaBytes = 0
  ) => {
    const totalBytes = reviewMediaBytes
      + sourceProgress.uploadedBytes
      + assetProgress.uploadedBytes
      + sourceProgress.queuedBytes
      + assetProgress.queuedBytes;
    if (totalBytes > sessionMediaUploadTotalBytesRef.current) {
      setSessionMediaUploadTotalBytesValue(totalBytes);
    }
  }, [setSessionMediaUploadTotalBytesValue]);

  const loadReviewData = useCallback(async ({ initializeDraft = true }: { initializeDraft?: boolean } = {}) => {
    if (!sessionCode) return;

    try {
      const token = await getToken();
      const response = await fetch(`/api/products/reference-packs/sessions/${sessionCode}/review`, {
        headers: authHeaders(token),
      });

      if (!response.ok) {
        throw new Error('Unable to load captured stills for this event');
      }

      const result = await response.json() as ReviewData;
      console.info('[ReferencePackPage] review data loaded', {
        sessionCode,
        captureCount: result.captures?.length ?? 0,
        recordingCount: result.recordings?.length ?? 0,
        recordings: (result.recordings ?? []).map((recording: ModelRecording) => ({
          id: recording.id,
          label: recording.label,
          status: recording.status,
          hasVideo: recording.hasVideo,
          contentType: recording.contentType,
          releaseStatus: recording.releaseStatus,
          durationSeconds: recording.durationSeconds,
          fileSizeBytes: recording.fileSizeBytes,
          startedAt: recording.startedAt,
        })),
        productCount: result.products?.length ?? 0,
      });

      setData(result);

      if (!initializeDraft) {
        return;
      }

      const fallbackProduct = result.products[0] as CaptureProduct | undefined;
      const product = (
        requestedProductId
          ? result.products.find((existingProduct: CaptureProduct) => existingProduct.id === requestedProductId)
          : null
      ) ?? fallbackProduct;
      const initialCaptureIds = sortedIds(
        result.captures
          .filter((capture: ReferencePackAsset) => capture.curationStatus === 'selected')
          .map((capture: ReferencePackAsset) => capture.id)
      );
      const initialRecordingIds = sortedIds(product?.recordingIds ?? []);
      const initialAttendeePrice = product?.attendeePriceCents !== null && product?.attendeePriceCents !== undefined
        ? (product.attendeePriceCents / 100).toFixed(2)
        : '0';
      const initialPublicPrice = product?.publicPriceCents !== null && product?.publicPriceCents !== undefined
        ? (product.publicPriceCents / 100).toFixed(2)
        : product?.priceCents !== null && product?.priceCents !== undefined
          ? (product.priceCents / 100).toFixed(2)
          : '10';
      const initialDraft: ProductDraft = {
        title: product?.title ?? getDefaultProductTitle(result.room.name),
        description: product?.description ?? '',
        attendeePrice: initialAttendeePrice,
        publicPrice: initialPublicPrice,
        license: product?.license ?? 'classroom_reference',
        productVisibility: product?.productVisibility ?? 'anyone',
        showOnProfile: Boolean(product?.showOnProfile),
        status: product?.status ?? 'draft',
        requiresBuyerApproval: Boolean(product?.requiresBuyerApproval),
        accessDurationCount: product?.accessDurationCount ? String(product.accessDurationCount) : DEFAULT_ACCESS_DURATION_COUNT,
        accessDurationUnit: product?.accessDurationUnit ?? DEFAULT_ACCESS_DURATION_UNIT,
        coverImageUrl: product?.coverImageUrl ?? null,
        captureIds: initialCaptureIds,
        recordingIds: initialRecordingIds,
      };

      setTitle(initialDraft.title);
      setDescription(initialDraft.description);
      setStatus(initialDraft.status);
      setEditingProductId(product?.id ?? null);
      setSelectedIds(new Set(initialDraft.captureIds));
      setSelectedRecordingIds(new Set(initialDraft.recordingIds));
      setAttendeePrice(initialDraft.attendeePrice);
      setPublicPrice(initialDraft.publicPrice);
      setLicense(initialDraft.license);
      setProductVisibility(initialDraft.productVisibility);
      setShowOnProfile(initialDraft.showOnProfile);
      setRequiresBuyerApproval(initialDraft.requiresBuyerApproval);
      setCoverImageUrl(initialDraft.coverImageUrl);
      setAccessDurationCount(initialDraft.accessDurationCount);
      setAccessDurationUnit(initialDraft.accessDurationUnit);
      setLastSavedDraft(initialDraft);
    } catch (err) {
      console.error('Error loading reference pack assets:', err);
      setError(err instanceof Error ? err.message : 'Failed to load captures');
    } finally {
      if (initializeDraft) {
        setLoading(false);
      }
    }
  }, [sessionCode, getToken, requestedProductId]);

  useEffect(() => {
    void loadReviewData();
  }, [loadReviewData]);

  const refreshSessionMediaUploadProgress = useCallback(async () => {
    if (!sessionCode) return;

    const [progress, assetProgress] = await Promise.all([
      getQueuedSourceRecordingProgress(sessionCode),
      getQueuedReferencePackAssetProgress(sessionCode),
    ]);
    setSourceRecordingProgress((current) => (
      sourceRecordingProgressChanged(current, progress) ? progress : current
    ));
    setReferencePackAssetProgress(assetProgress);
    updateSessionMediaUploadTotal(progress, assetProgress, getReviewMediaUploadBytes(data));

    if (!progress.hasOutstandingWork && !assetProgress.hasOutstandingWork && !sessionMediaUploadInProgressRef.current) {
      setSessionMediaUploadTotalBytesValue(0);
    }
  }, [data, sessionCode, setSessionMediaUploadTotalBytesValue, updateSessionMediaUploadTotal]);

  const uploadPendingSessionMedia = useCallback(async () => {
    if (!sessionCode || sessionMediaUploadInProgressRef.current) return;

    sessionMediaUploadInProgressRef.current = true;

    try {
      const [initialSourceProgress, initialAssetProgress] = await Promise.all([
        getQueuedSourceRecordingProgress(sessionCode),
        getQueuedReferencePackAssetProgress(sessionCode),
      ]);
      setSourceRecordingProgress((current) => (
        sourceRecordingProgressChanged(current, initialSourceProgress) ? initialSourceProgress : current
      ));
      setReferencePackAssetProgress(initialAssetProgress);
      if (!initialSourceProgress.hasOutstandingWork && !initialAssetProgress.hasOutstandingWork) {
        setSessionMediaUploadError(null);
        setSessionMediaUploadTotalBytesValue(0);
        return;
      }

      setSessionMediaUploading(true);
      setSessionMediaUploadError(null);
      setSessionMediaUploadTotalBytesValue(
        getReviewMediaUploadBytes(data)
        + initialSourceProgress.uploadedBytes
        + initialAssetProgress.uploadedBytes
        + initialSourceProgress.queuedBytes
        + initialAssetProgress.queuedBytes
      );

      await uploadQueuedReferencePackAssets({
        roomCode: sessionCode,
        uploadPhase: 'reference_pack',
        limit: Number.MAX_SAFE_INTEGER,
        onProgress: setReferencePackAssetProgress,
      });

      await loadReviewData({ initializeDraft: false });
      setReferencePackAssetProgress(await getQueuedReferencePackAssetProgress(sessionCode));

      const progress = await uploadQueuedSourceRecordings({
        roomCode: sessionCode,
        uploadPhase: 'reference_pack',
        finalizeOpenSegments: true,
        getToken,
        onProgress: (nextProgress) => {
          setSourceRecordingProgress((current) => (
            sourceRecordingProgressChanged(current, nextProgress) ? nextProgress : current
          ));
        },
      });
      setSourceRecordingProgress((current) => (
        sourceRecordingProgressChanged(current, progress) ? progress : current
      ));
      await loadReviewData({ initializeDraft: false });
      setSourceRecordingProgress(await getQueuedSourceRecordingProgress(sessionCode));
    } catch (err) {
      console.error('Error uploading saved session media:', err);
      setSessionMediaUploadError(err instanceof Error ? err.message : 'Failed to upload saved session media');
      await refreshSessionMediaUploadProgress();
    } finally {
      sessionMediaUploadInProgressRef.current = false;
      setSessionMediaUploading(false);
    }
  }, [data, sessionCode, getToken, loadReviewData, refreshSessionMediaUploadProgress, setSessionMediaUploadTotalBytesValue]);

  useEffect(() => {
    void refreshSessionMediaUploadProgress();
    void uploadPendingSessionMedia();
  }, [refreshSessionMediaUploadProgress, uploadPendingSessionMedia]);

  const activeCaptures = useMemo(() => {
    return data?.captures.filter((capture) => capture.curationStatus !== 'rejected') ?? [];
  }, [data]);

  const selectedCaptures = activeCaptures.filter((capture) => selectedIds.has(capture.id));
  const poseGroups = useMemo<PoseCaptureGroup[]>(() => {
    const groups = new Map<string, PoseCaptureGroup>();
    for (const capture of activeCaptures) {
      const key = getCapturePoseKey(capture);
      const existing = groups.get(key);
      if (existing) {
        existing.captures.push(capture);
      } else {
        groups.set(key, {
          key,
          label: getCapturePoseLabel(capture),
          captures: [capture],
        });
      }
    }
    return Array.from(groups.values());
  }, [activeCaptures]);
  const poseFilteredCaptures = useMemo(() => (
    activePoseKey === 'all'
      ? activeCaptures
      : poseGroups.find((group) => group.key === activePoseKey)?.captures ?? []
  ), [activeCaptures, activePoseKey, poseGroups]);
  const allCameraGroups = useMemo(() => buildCameraCaptureGroups(activeCaptures), [activeCaptures]);
  const cameraLabelByKey = useMemo(() => {
    return new Map(allCameraGroups.map((group) => [group.key, group.label]));
  }, [allCameraGroups]);
  const cameraLabelByCaptureId = useMemo(() => {
    const labels = new Map<string, string>();
    for (const group of allCameraGroups) {
      for (const capture of group.captures) {
        labels.set(capture.id, group.label);
      }
    }
    return labels;
  }, [allCameraGroups]);
  const cameraGroups = useMemo<CameraCaptureGroup[]>(() => {
    const groups = new Map<string, CameraCaptureGroup>();
    for (const capture of poseFilteredCaptures) {
      const key = getCaptureCameraKey(capture);
      const existing = groups.get(key);
      if (existing) {
        existing.captures.push(capture);
      } else {
        groups.set(key, {
          key,
          label: cameraLabelByKey.get(key) ?? getCaptureCameraLabel(capture),
          captures: [capture],
        });
      }
    }
    return Array.from(groups.values());
  }, [cameraLabelByKey, poseFilteredCaptures]);
  const productPreviewCaptures = useMemo(() => {
    return poseGroups
      .map((group) => group.captures.find((capture) => selectedIds.has(capture.id)))
      .filter((capture): capture is ReferencePackAsset => Boolean(capture));
  }, [poseGroups, selectedIds]);
  const selectedRecordings = useMemo(() => {
    return data?.recordings.filter((recording) => selectedRecordingIds.has(recording.id)) ?? [];
  }, [data, selectedRecordingIds]);
  const uploadedRecordings = useMemo(() => {
    return data?.recordings.filter((recording) => recording.recordingKind !== 'backup') ?? [];
  }, [data]);
  const backupRecordings = useMemo(() => {
    return data?.recordings.filter((recording) => recording.recordingKind === 'backup') ?? [];
  }, [data]);
  const currentDraft = useMemo<ProductDraft>(() => ({
    title,
    description,
    attendeePrice: normalizePriceInput(attendeePrice),
    publicPrice: normalizePriceInput(publicPrice),
    license,
    productVisibility,
    showOnProfile,
    status,
    requiresBuyerApproval,
    accessDurationCount,
    accessDurationUnit,
    coverImageUrl,
    captureIds: sortedIds(selectedIds),
    recordingIds: sortedIds(selectedRecordingIds),
  }), [
    title,
    description,
    attendeePrice,
    publicPrice,
    license,
    productVisibility,
    showOnProfile,
    status,
    requiresBuyerApproval,
    accessDurationCount,
    accessDurationUnit,
    coverImageUrl,
    selectedIds,
    selectedRecordingIds,
  ]);
  const hasProductChanges = !lastSavedDraft
    || coverImageFile !== null
    || currentDraft.title !== lastSavedDraft.title
    || currentDraft.description !== lastSavedDraft.description
    || currentDraft.attendeePrice !== normalizePriceInput(lastSavedDraft.attendeePrice)
    || currentDraft.publicPrice !== normalizePriceInput(lastSavedDraft.publicPrice)
    || currentDraft.license !== lastSavedDraft.license
    || currentDraft.productVisibility !== lastSavedDraft.productVisibility
    || currentDraft.showOnProfile !== lastSavedDraft.showOnProfile
    || currentDraft.status !== lastSavedDraft.status
    || currentDraft.requiresBuyerApproval !== lastSavedDraft.requiresBuyerApproval
    || currentDraft.accessDurationCount !== lastSavedDraft.accessDurationCount
    || currentDraft.accessDurationUnit !== lastSavedDraft.accessDurationUnit
    || currentDraft.coverImageUrl !== lastSavedDraft.coverImageUrl
    || !areStringArraysEqual(currentDraft.captureIds, lastSavedDraft.captureIds)
    || !areStringArraysEqual(currentDraft.recordingIds, lastSavedDraft.recordingIds);
  const visibleCaptures = activeCameraKey === 'all'
    ? poseFilteredCaptures
    : poseFilteredCaptures.filter((capture) => getCaptureCameraKey(capture) === activeCameraKey);
  const lightboxCapture = activeCaptures.find((capture) => capture.id === lightboxCaptureId) ?? null;
  const lightboxCapturePoseLabel = lightboxCapture ? getCapturePoseLabel(lightboxCapture) : null;
  const lightboxCaptureCameraLabel = lightboxCapture
    ? cameraLabelByCaptureId.get(lightboxCapture.id) ?? getCaptureCameraLabel(lightboxCapture)
    : null;
  const shareableReferencePackUrl = useMemo(() => {
    if (!sessionCode || !editingProductId || typeof window === 'undefined') return null;

    const url = new URL(`/s/${sessionCode}/summary`, window.location.origin);
    url.hash = `reference-pack-${editingProductId}`;
    return url.toString();
  }, [editingProductId, sessionCode]);

  useEffect(() => {
    if (activePoseKey === 'all') return;
    if (!poseGroups.some((group) => group.key === activePoseKey)) {
      setActivePoseKey('all');
    }
  }, [activePoseKey, poseGroups]);

  useEffect(() => {
    if (activeCameraKey === 'all') return;
    if (!cameraGroups.some((group) => group.key === activeCameraKey)) {
      setActiveCameraKey('all');
    }
  }, [activeCameraKey, cameraGroups]);

  useEffect(() => {
    if (!lightboxCaptureId) return;
    if (!activeCaptures.some((capture) => capture.id === lightboxCaptureId)) {
      setLightboxCaptureId(null);
    }
  }, [activeCaptures, lightboxCaptureId]);

  useEffect(() => {
    if (!lightboxCaptureId) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setLightboxCaptureId(null);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxCaptureId]);

  const updateCaptureStatus = async (captureId: string, nextStatus: CaptureStatus) => {
    if (!sessionCode || !data) return;

    const previousData = data;
    const previousSelectedIds = selectedIds;

    setData((current) => current ? {
      ...current,
      captures: current.captures.map((capture) =>
        capture.id === captureId ? { ...capture, curationStatus: nextStatus } : capture
      ),
    } : current);

    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (nextStatus === 'selected') {
        next.add(captureId);
      } else {
        next.delete(captureId);
      }
      return next;
    });

    try {
      const token = await getToken();
      const response = await fetch(`/api/products/reference-packs/sessions/${sessionCode}/assets/${captureId}`, {
        method: 'PATCH',
        headers: jsonAuthHeaders(token),
        body: JSON.stringify({ status: nextStatus }),
      });

      if (!response.ok) {
        throw new Error('Failed to save photo selection');
      }
    } catch (err) {
      console.error('Error updating capture:', err);
      setData(previousData);
      setSelectedIds(previousSelectedIds);
      alert(err instanceof Error ? err.message : 'Failed to save photo selection');
    }
  };

  const toggleSelected = (capture: ReferencePackAsset) => {
    const isSelected = selectedIds.has(capture.id);
    updateCaptureStatus(capture.id, isSelected ? 'undecided' : 'selected');
  };

  const toggleRecordingSelected = (recording: ModelRecording) => {
    if (recording.status !== 'completed' || !recording.hasVideo) return;

    setSelectedRecordingIds((prev) => {
      const next = new Set(prev);
      if (next.has(recording.id)) {
        next.delete(recording.id);
      } else {
        next.add(recording.id);
      }
      return next;
    });
  };

  const handleCoverImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setCoverImageFile(file);
    const reader = new FileReader();
    reader.onloadend = () => {
      setCoverImagePreviewUrl(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const removeCoverImage = () => {
    setCoverImageFile(null);
    setCoverImageUrl(null);
    setCoverImagePreviewUrl(null);
  };

  const cancelProductChanges = () => {
    if (!lastSavedDraft) return;

    setTitle(lastSavedDraft.title);
    setDescription(lastSavedDraft.description);
    setAttendeePrice(lastSavedDraft.attendeePrice);
    setPublicPrice(lastSavedDraft.publicPrice);
    setLicense(lastSavedDraft.license);
    setProductVisibility(lastSavedDraft.productVisibility);
    setShowOnProfile(lastSavedDraft.showOnProfile);
    setStatus(lastSavedDraft.status);
    setRequiresBuyerApproval(lastSavedDraft.requiresBuyerApproval);
    setAccessDurationCount(lastSavedDraft.accessDurationCount);
    setAccessDurationUnit(lastSavedDraft.accessDurationUnit);
    setCoverImageUrl(lastSavedDraft.coverImageUrl);
    setCoverImageFile(null);
    setCoverImagePreviewUrl(null);
    setSelectedIds(new Set(lastSavedDraft.captureIds));
    setSelectedRecordingIds(new Set(lastSavedDraft.recordingIds));
    setActivePoseKey('all');
  };

  const uploadCoverImage = async (token: string | null): Promise<string | null> => {
    if (!sessionCode || !coverImageFile) return coverImageUrl;

    const formData = new FormData();
    formData.append('image', coverImageFile);

    const response = await fetch(`/api/products/reference-packs/sessions/${sessionCode}/product-cover`, {
      method: 'POST',
      headers: authHeaders(token),
      body: formData,
    });

    if (!response.ok) {
      throw new Error('Failed to upload cover photo');
    }

    const result = await response.json();
    return result.imageUrl ?? null;
  };

  const uploadProductMedia = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.currentTarget;
    const files = Array.from(input.files ?? []);
    input.value = '';
    if (!sessionCode || files.length === 0) return;

    const imageFiles = files.filter(isImageUpload);
    const videoFiles = files.filter((file) => !isImageUpload(file) && isVideoUpload(file));
    const unsupportedFiles = files.filter((file) => !isImageUpload(file) && !isVideoUpload(file));

    if (unsupportedFiles.length > 0) {
      alert(`Unsupported file type: ${unsupportedFiles.map((file) => file.name).join(', ')}`);
    }

    if (imageFiles.length === 0 && videoFiles.length === 0) return;

    setUploadingPhotos(imageFiles.length > 0);
    setUploadingVideos(videoFiles.length > 0);
    try {
      const token = await getToken();

      const uploadedCaptures: ReferencePackAsset[] = [];
      for (const file of imageFiles) {
        const { width, height } = await getImageDimensions(file);
        const formData = new FormData();
        formData.append('file', file);
        if (width !== null) formData.append('width', String(width));
        if (height !== null) formData.append('height', String(height));

        const response = await fetch(`/api/products/reference-packs/sessions/${sessionCode}/product-photos`, {
          method: 'POST',
          headers: authHeaders(token),
          body: formData,
        });

        if (!response.ok) {
          const authMessage = signInMessageForAuthStatus(response, 'Please sign in again to upload');
          if (authMessage) throw new Error(authMessage);
          throw new Error(`Failed to upload ${file.name}`);
        }

        const result = await response.json();
        uploadedCaptures.push(result.capture);
      }

      const uploadedRecordings: ModelRecording[] = [];
      for (const file of videoFiles) {
        const formData = new FormData();
        formData.append('file', file);

        const response = await fetch(`/api/products/reference-packs/sessions/${sessionCode}/product-videos`, {
          method: 'POST',
          headers: authHeaders(token),
          body: formData,
        });

        if (!response.ok) {
          const authMessage = signInMessageForAuthStatus(response, 'Please sign in again to upload');
          if (authMessage) throw new Error(authMessage);
          throw new Error(`Failed to upload ${file.name}`);
        }

        const result = await response.json();
        uploadedRecordings.push(result.recording);
      }

      if (uploadedCaptures.length > 0 || uploadedRecordings.length > 0) {
        setData((prev) => prev ? {
          ...prev,
          captures: [...prev.captures, ...uploadedCaptures],
          recordings: [...uploadedRecordings, ...prev.recordings],
        } : prev);
      }

      if (uploadedCaptures.length > 0) {
        setSelectedIds((prev) => {
          const next = new Set(prev);
          uploadedCaptures.forEach((capture) => next.add(capture.id));
          return next;
        });
        setActivePoseKey('all');
      }

      if (uploadedRecordings.length > 0) {
        setSelectedRecordingIds((prev) => {
          const next = new Set(prev);
          uploadedRecordings.forEach((recording) => next.add(recording.id));
          return next;
        });
      }
    } catch (err) {
      console.error('Error uploading product media:', err);
      alert(err instanceof Error ? err.message : 'Failed to upload');
    } finally {
      setUploadingPhotos(false);
      setUploadingVideos(false);
    }
  };

  const downloadCapture = async (
    capture: ReferencePackAsset,
    labels: { poseLabel: string; cameraLabel: string }
  ) => {
    if (!capture.imageUrl) return;

    const filename = getCaptureDownloadFilename(capture, labels);
    setDownloadingCaptureId(capture.id);

    try {
      const response = await fetch(capture.imageUrl);
      if (!response.ok) {
        throw new Error('Photo not available for download');
      }

      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      triggerBrowserDownload(objectUrl, filename);
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
    } catch (err) {
      console.error('Error downloading photo:', err);
      triggerBrowserDownload(capture.imageUrl, filename, true);
    } finally {
      setDownloadingCaptureId(null);
    }
  };

  const downloadRecording = async (recording: ModelRecording) => {
    if (!recording.hasVideo || recording.status !== 'completed') return;

    setDownloadingRecordingId(recording.id);
    try {
      const token = await getToken();

      const response = await fetch(`/api/recordings/id/${recording.id}/download`, {
        headers: authHeaders(token),
      });

      if (!response.ok) {
        const authMessage = signInMessageForAuthStatus(response, 'Please sign in again to download this recording');
        if (authMessage) throw new Error(authMessage);
        throw new Error('Recording not available for download');
      }

      const result = await response.json();
      if (!result.downloadUrl) {
        throw new Error('Recording not available for download');
      }

      const link = document.createElement('a');
      link.href = result.downloadUrl;
      link.download = result.filename || 'session-recording.mp4';
      link.rel = 'noopener';
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Error downloading recording:', err);
      alert(err instanceof Error ? err.message : 'Failed to download recording');
    } finally {
      setDownloadingRecordingId(null);
    }
  };

  const deleteRecording = async (recording: ModelRecording) => {
    if (!sessionCode || !data) return;

    const recordingName = getRecordingDisplayName(recording);
    const confirmed = window.confirm(
      `Permanently delete "${recordingName}"?\n\nThis removes the video from this event and any reference packs that use it. This cannot be undone.`
    );
    if (!confirmed) return;

    setDeletingRecordingId(recording.id);
    try {
      const token = await getToken();

      const response = await fetch(`/api/products/reference-packs/sessions/${sessionCode}/recordings/${recording.id}`, {
        method: 'DELETE',
        headers: authHeaders(token),
      });

      if (!response.ok) {
        const result = await response.json().catch(() => null);
        const authMessage = signInMessageForAuthStatus(response, 'Please sign in again to delete this video');
        if (authMessage) throw new Error(authMessage);
        throw new Error(result?.error || 'Failed to delete video');
      }

      setData((current) => current ? {
        ...current,
        recordings: current.recordings.filter((item) => item.id !== recording.id),
        products: current.products.map((product) => ({
          ...product,
          recordingIds: product.recordingIds.filter((recordingId) => recordingId !== recording.id),
        })),
      } : current);
      setSelectedRecordingIds((current) => {
        const next = new Set(current);
        next.delete(recording.id);
        return next;
      });
      setLastSavedDraft((current) => current ? {
        ...current,
        recordingIds: current.recordingIds.filter((recordingId) => recordingId !== recording.id),
      } : current);
    } catch (err) {
      console.error('Error deleting recording:', err);
      alert(err instanceof Error ? err.message : 'Failed to delete video');
    } finally {
      setDeletingRecordingId(null);
    }
  };

  const deleteProduct = async () => {
    if (!editingProductId || !data) return;

    const productName = title.trim() || 'this reference pack';
    const confirmed = window.confirm(
      `Permanently delete "${productName}"?\n\nAnyone with access will lose access to this reference pack. Photos and videos will stay in the event so you can create a new pack. This cannot be undone.`
    );
    if (!confirmed) return;

    setDeletingProduct(true);
    try {
      const token = await getToken();

      const response = await fetch(`/api/products/reference-packs/${editingProductId}`, {
        method: 'DELETE',
        headers: authHeaders(token),
      });

      if (!response.ok) {
        const result = await response.json().catch(() => null);
        const authMessage = signInMessageForAuthStatus(response, 'Please sign in again to delete this reference pack');
        if (authMessage) throw new Error(authMessage);
        throw new Error(result?.error || 'Failed to delete reference pack');
      }

      const initialCaptureIds = sortedIds(
        data.captures
          .filter((capture) => capture.curationStatus === 'selected')
          .map((capture) => capture.id)
      );
      const nextDraft: ProductDraft = {
        title: getDefaultProductTitle(data.room.name),
        description: '',
        attendeePrice: '0',
        publicPrice: '10',
        license: 'classroom_reference',
        productVisibility: 'anyone',
        showOnProfile: false,
        status: 'draft',
        requiresBuyerApproval: false,
        accessDurationCount: DEFAULT_ACCESS_DURATION_COUNT,
        accessDurationUnit: DEFAULT_ACCESS_DURATION_UNIT,
        coverImageUrl: null,
        captureIds: initialCaptureIds,
        recordingIds: [],
      };

      setData((current) => current ? {
        ...current,
        products: current.products.filter((product) => product.id !== editingProductId),
      } : current);
      setEditingProductId(null);
      setTitle(nextDraft.title);
      setDescription(nextDraft.description);
      setAttendeePrice(nextDraft.attendeePrice);
      setPublicPrice(nextDraft.publicPrice);
      setLicense(nextDraft.license);
      setProductVisibility(nextDraft.productVisibility);
      setShowOnProfile(nextDraft.showOnProfile);
      setStatus(nextDraft.status);
      setRequiresBuyerApproval(nextDraft.requiresBuyerApproval);
      setAccessDurationCount(nextDraft.accessDurationCount);
      setAccessDurationUnit(nextDraft.accessDurationUnit);
      setCoverImageUrl(nextDraft.coverImageUrl);
      setCoverImageFile(null);
      setCoverImagePreviewUrl(null);
      setSelectedIds(new Set(nextDraft.captureIds));
      setSelectedRecordingIds(new Set(nextDraft.recordingIds));
      setLastSavedDraft(nextDraft);
      setActiveTab('settings');
    } catch (err) {
      console.error('Error deleting product:', err);
      alert(err instanceof Error ? err.message : 'Failed to delete reference pack');
    } finally {
      setDeletingProduct(false);
    }
  };

  const setPublishState = async (action: 'unpublish' | 'republish') => {
    if (!editingProductId) return;

    setSaving(true);
    try {
      const token = await getToken();

      const response = await fetch(`/api/products/reference-packs/${editingProductId}/${action}`, {
        method: 'POST',
        headers: authHeaders(token),
      });

      if (!response.ok) {
        const result = await response.json().catch(() => null);
        const authMessage = signInMessageForAuthStatus(response, 'Please sign in again to update this reference pack');
        if (authMessage) throw new Error(authMessage);
        throw new Error(result?.error || `Failed to ${action} reference pack`);
      }

      const result = await response.json();
      const nextStatus = (result.product?.status as ProductStatus | undefined)
        ?? (action === 'unpublish' ? 'unpublished' : 'ready');

      setStatus(nextStatus);
      setLastSavedDraft((current) => current ? { ...current, status: nextStatus } : current);
      setData((current) => current ? {
        ...current,
        products: current.products.map((product) => (
          product.id === editingProductId ? { ...product, status: nextStatus } : product
        )),
      } : current);
    } catch (err) {
      console.error(`Error trying to ${action} reference pack:`, err);
      alert(err instanceof Error ? err.message : `Failed to ${action} reference pack`);
    } finally {
      setSaving(false);
    }
  };

  const copyShareableLink = async () => {
    if (!shareableReferencePackUrl) return;

    try {
      await navigator.clipboard.writeText(shareableReferencePackUrl);
      setShareLinkCopied(true);
      window.setTimeout(() => setShareLinkCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy reference pack link:', err);
      window.alert('Copy failed. Select the link and copy it manually.');
    }
  };

  const accessDurationNumber = Number(accessDurationCount);
  const hasValidAccessDuration = Number.isInteger(accessDurationNumber) && accessDurationNumber > 0;
  const canSaveProduct = (selectedIds.size > 0 || selectedRecordingIds.size > 0) && title.trim().length > 0 && hasValidAccessDuration;
  const sessionMediaReviewBytes = getReviewMediaUploadBytes(data);
  const sessionMediaQueuedBytes = sourceRecordingProgress.queuedBytes + referencePackAssetProgress.queuedBytes;
  const sessionMediaLocalUploadedBytes = sourceRecordingProgress.uploadedBytes + referencePackAssetProgress.uploadedBytes;
  const sessionMediaActiveUploadBytes = sourceRecordingProgress.activeUploadBytes + referencePackAssetProgress.activeUploadBytes;
  const sessionMediaHasOutstandingWork = sourceRecordingProgress.hasOutstandingWork || referencePackAssetProgress.hasOutstandingWork;
  const sessionMediaProgressTotalBytes = Math.max(
    sessionMediaUploadTotalBytes,
    sessionMediaReviewBytes + sessionMediaLocalUploadedBytes + sessionMediaQueuedBytes
  );
  const sessionMediaUploadedBytes = Math.min(
    sessionMediaProgressTotalBytes,
    Math.max(0, sessionMediaReviewBytes + sessionMediaLocalUploadedBytes + sessionMediaActiveUploadBytes)
  );
  const sessionMediaUploadPercent = sessionMediaProgressTotalBytes > 0
    ? Math.min(100, Math.round((sessionMediaUploadedBytes / sessionMediaProgressTotalBytes) * 100))
    : sessionMediaHasOutstandingWork ? 0 : 100;
  const sessionMediaUploadPhotoCount = (data?.captures.length ?? 0)
    + referencePackAssetProgress.totalCaptures
    + referencePackAssetProgress.uploadedCaptures;
  const sessionMediaUploadVideoCount = (data?.recordings.length ?? 0)
    + sourceRecordingProgress.totalSegments
    + sourceRecordingProgress.uploadedSegments;
  const sessionMediaUploadedPhotoCount = (data?.captures.length ?? 0) + referencePackAssetProgress.uploadedCaptures;
  const sessionMediaUploadedVideoCount = (data?.recordings.length ?? 0) + sourceRecordingProgress.uploadedSegments;
  const sessionMediaTransferRate = formatTransferRate(sessionMediaUploadEstimate.bytesPerSecond);
  const sessionMediaEta = formatEta(sessionMediaUploadEstimate.secondsRemaining);
  const canSaveChanges = canSaveProduct
    && status !== 'withdrawn'
    && !sessionMediaHasOutstandingWork
    && !sessionMediaUploading
    && (hasProductChanges || !editingProductId);

  useEffect(() => {
    if (!sessionMediaUploading || sessionMediaProgressTotalBytes <= 0) {
      sessionMediaUploadSamplesRef.current = [];
      setSessionMediaUploadEstimate({ bytesPerSecond: null, secondsRemaining: null });
      return;
    }

    const now = Date.now();
    const samples = [
      ...sessionMediaUploadSamplesRef.current,
      { at: now, uploadedBytes: sessionMediaUploadedBytes },
    ].filter((sample) => now - sample.at <= 30_000);
    sessionMediaUploadSamplesRef.current = samples;

    const firstUsefulSample = samples.find((sample) => (
      sample.uploadedBytes < sessionMediaUploadedBytes &&
      now - sample.at >= 3000
    ));

    if (!firstUsefulSample) {
      setSessionMediaUploadEstimate({ bytesPerSecond: null, secondsRemaining: null });
      return;
    }

    const elapsedSeconds = (now - firstUsefulSample.at) / 1000;
    const uploadedDelta = sessionMediaUploadedBytes - firstUsefulSample.uploadedBytes;
    const bytesPerSecond = elapsedSeconds > 0 ? uploadedDelta / elapsedSeconds : 0;
    const remainingBytes = Math.max(0, sessionMediaProgressTotalBytes - sessionMediaUploadedBytes);

    setSessionMediaUploadEstimate({
      bytesPerSecond: bytesPerSecond > 0 ? bytesPerSecond : null,
      secondsRemaining: bytesPerSecond > 0 ? remainingBytes / bytesPerSecond : null,
    });
  }, [sessionMediaProgressTotalBytes, sessionMediaUploadedBytes, sessionMediaUploading]);

  const saveProduct = async (statusOverride?: ProductStatus) => {
    if (!sessionCode || (selectedIds.size === 0 && selectedRecordingIds.size === 0)) return;

    const nextStatus = statusOverride ?? status;
    // Withdrawn packs are admin-controlled; the editor never writes that state.
    if (nextStatus === 'withdrawn') return;
    setSaving(true);
    try {
      const token = await getToken();
      const savedCoverImageUrl = await uploadCoverImage(token);
      const response = await fetch(`/api/products/reference-packs/sessions/${sessionCode}/packs`, {
        method: 'POST',
        headers: jsonAuthHeaders(token),
        body: JSON.stringify({
          productId: editingProductId,
          title,
          description: description.trim() || null,
          accessType: 'sellable',
          attendeePriceCents: Math.round((Number(attendeePrice) || 0) * 100),
          priceCents: Math.round((Number(publicPrice) || 0) * 100),
          currency: 'USD',
          license,
          productVisibility,
          showOnProfile,
          status: nextStatus,
          coverCaptureId: productPreviewCaptures[0]?.id ?? null,
          coverImageUrl: savedCoverImageUrl,
          accessDurationCount: Math.max(1, Math.round(Number(accessDurationCount) || 1)),
          accessDurationUnit,
          captureIds: Array.from(selectedIds),
          recordingIds: Array.from(selectedRecordingIds),
          requiresBuyerApproval,
        }),
      });

      if (!response.ok) {
        const result = await response.json().catch(() => null);
        const authMessage = signInMessageForAuthStatus(response, 'Please sign in again to save this product');
        if (authMessage) throw new Error(authMessage);
        throw new Error(result?.error || 'Failed to save product');
      }

      const result = await response.json();
      const savedProduct = result.product as CaptureProduct;
      const nextSavedDraft: ProductDraft = {
        title: savedProduct.title ?? title,
        description: savedProduct.description ?? '',
        attendeePrice: savedProduct.attendeePriceCents !== null && savedProduct.attendeePriceCents !== undefined
          ? (savedProduct.attendeePriceCents / 100).toFixed(2)
          : normalizePriceInput(attendeePrice),
        publicPrice: savedProduct.publicPriceCents !== null && savedProduct.publicPriceCents !== undefined
          ? (savedProduct.publicPriceCents / 100).toFixed(2)
          : savedProduct.priceCents !== null && savedProduct.priceCents !== undefined
            ? (savedProduct.priceCents / 100).toFixed(2)
            : normalizePriceInput(publicPrice),
        license: savedProduct.license ?? license,
        productVisibility: savedProduct.productVisibility ?? productVisibility,
        showOnProfile: Boolean(savedProduct.showOnProfile),
        status: savedProduct.status ?? nextStatus,
        requiresBuyerApproval: Boolean(savedProduct.requiresBuyerApproval),
        accessDurationCount: savedProduct.accessDurationCount ? String(savedProduct.accessDurationCount) : accessDurationCount,
        accessDurationUnit: savedProduct.accessDurationUnit ?? accessDurationUnit,
        coverImageUrl: savedProduct.coverImageUrl ?? savedCoverImageUrl,
        captureIds: sortedIds(savedProduct.captureIds ?? selectedIds),
        recordingIds: sortedIds(savedProduct.recordingIds ?? selectedRecordingIds),
      };

      setEditingProductId(savedProduct.id);
      setData((prev) => {
        if (!prev) return prev;
        const otherProducts = prev.products.filter((product) => product.id !== savedProduct.id);
        return { ...prev, products: [savedProduct, ...otherProducts] };
      });
      setTitle(nextSavedDraft.title);
      setDescription(nextSavedDraft.description);
      setAttendeePrice(nextSavedDraft.attendeePrice);
      setPublicPrice(nextSavedDraft.publicPrice);
      setLicense(nextSavedDraft.license);
      setProductVisibility(nextSavedDraft.productVisibility);
      setShowOnProfile(nextSavedDraft.showOnProfile);
      setStatus(nextSavedDraft.status);
      setRequiresBuyerApproval(nextSavedDraft.requiresBuyerApproval);
      setAccessDurationCount(nextSavedDraft.accessDurationCount);
      setAccessDurationUnit(nextSavedDraft.accessDurationUnit);
      setCoverImageUrl(nextSavedDraft.coverImageUrl);
      setCoverImageFile(null);
      setCoverImagePreviewUrl(null);
      setSelectedIds(new Set(nextSavedDraft.captureIds));
      setSelectedRecordingIds(new Set(nextSavedDraft.recordingIds));
      setLastSavedDraft(nextSavedDraft);
    } catch (err) {
      console.error('Error saving product:', err);
      alert(err instanceof Error ? err.message : 'Failed to save product');
    } finally {
      setSaving(false);
    }
  };

  const formatDuration = (seconds: number | null) => {
    if (seconds === null) return null;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${String(remainingSeconds).padStart(2, '0')}`;
  };
  const renderRecordingCard = (recording: ModelRecording) => {
    const selected = selectedRecordingIds.has(recording.id);
    const isCompleted = recording.status === 'completed';
    const isPlayable = isCompleted && recording.hasVideo;

    return (
      <article
        key={recording.id}
        className={`bg-panel border rounded-lg p-4 transition-colors ${
          selected ? 'border-text' : isPlayable ? 'border-line' : 'border-warn/40'
        }`}
      >
        <div className="space-y-4">
          <SecureVideoPlayer
            recordingId={recording.id}
            hasVideo={isPlayable}
            contentType={recording.contentType}
            status={recording.status}
            durationSeconds={recording.durationSeconds}
            startedAt={recording.startedAt}
            showDetailsFooter={false}
            allowDownload
            showDownloadButton={false}
          />
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2 mb-1">
                <h3 className="font-medium text-text">{getRecordingDisplayName(recording)}</h3>
                {recording.recordingKind === 'backup' && (
                  <span className="text-[11px] px-2 py-0.5 rounded-full bg-panel2 text-text-dim">
                    Backup
                  </span>
                )}
                {!isPlayable && (
                  <span className="text-[11px] px-2 py-0.5 rounded-full bg-panel2 text-text-dim">
                    {recording.status}
                  </span>
                )}
              </div>
              <p className="text-sm text-text-dim">
                {[
                  formatDuration(recording.durationSeconds),
                  recording.fileSizeBytes ? formatFileSize(recording.fileSizeBytes) : null,
                ].filter(Boolean).join(' · ')}
              </p>
              {!isCompleted && (
                <p className="text-xs text-warn mt-2">
                  This recording is not finalized yet, so it cannot be downloaded or added to a product.
                </p>
              )}
              {!recording.hasVideo && isCompleted && (
                <p className="text-xs text-warn mt-2">
                  The recording metadata is complete, but no saved video file is available yet.
                </p>
              )}
            </div>
            <div className="flex flex-wrap gap-2 sm:justify-end">
              <button
                type="button"
                onClick={() => downloadRecording(recording)}
                disabled={!isPlayable || downloadingRecordingId === recording.id}
                className="btn btn-sm shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {downloadingRecordingId === recording.id ? 'Preparing...' : 'Download'}
              </button>
              <button
                type="button"
                onClick={() => toggleRecordingSelected(recording)}
                disabled={!isPlayable}
                aria-pressed={selected}
                className={`btn btn-sm shrink-0 disabled:opacity-50 disabled:cursor-not-allowed ${
                  selected ? 'btn-accent' : ''
                }`}
              >
                {selected ? 'Added' : 'Add'}
              </button>
              <button
                type="button"
                onClick={() => deleteRecording(recording)}
                disabled={deletingRecordingId === recording.id}
                aria-label={`Delete video ${getRecordingDisplayName(recording)}`}
                className="btn btn-sm shrink-0 text-warn border-warn/40 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {deletingRecordingId === recording.id ? 'Deleting...' : 'Delete video'}
              </button>
            </div>
          </div>
        </div>
      </article>
    );
  };
  const showSessionMediaUploadPanel = sessionMediaHasOutstandingWork
    || sessionMediaUploading
    || Boolean(sessionMediaUploadError);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg">
        <div className="text-text-dim">Loading captures...</div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg">
        <div className="bg-panel border border-line rounded-xl p-6 max-w-md text-center">
          <h2 className="font-display text-xl font-medium text-text mb-2">Capture review unavailable</h2>
          <p className="text-text-dim mb-4">{error}</p>
          <button onClick={() => navigate('/recordings/review')} className="btn">Back to review</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bg flex flex-col">
      <NavBar
        variant="light"
        links={[
          { label: 'Drawing Events', href: '/events' },
          { label: 'Artists', href: '/artists' },
          { label: 'Models', href: '/models' },
          { label: 'Recordings', href: '/recordings/review', active: true },
        ]}
      />

      <main className="flex-1 w-full page-container pt-[104px] pb-8">
	        <div className="mb-7 flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
	          <div>
	            <h1 className="page-heading">
              Create a reference pack
            </h1>
            <p className="mt-3 max-w-2xl text-base text-text-dim">
              Reference packs can be sold on your profile and distributed to guests from your session.
            </p>
            <p className="text-text-dim mt-2">
	              {data.room.name} · {formatDate(data.room.scheduledAt)} · {pluralize(data.recordings.length, 'video')} captured · {pluralize(data.captures.length, 'photo')} captured
	            </p>
	          </div>
            <div className="flex flex-wrap items-center gap-2.5 lg:pt-1">
              <button
                type="button"
                onClick={() => navigate(`/s/${sessionCode}/summary`)}
                className="btn"
              >
                View event summary
              </button>
            </div>
        </div>

        {showSessionMediaUploadPanel && (
          <section className="mb-6 rounded-lg border border-line bg-panel p-4">
            <div className="min-w-0">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <h2 className="font-medium text-text">
                    {sessionMediaUploadError
                      ? 'Media upload needs attention'
                      : sessionMediaUploading
                        ? 'Uploading session media'
                        : 'Session media saved locally'}
                  </h2>
                  <p className="mt-1 text-sm text-text-dim">
                    Keep this page open while the model camera media finishes uploading. Uploaded items will appear below automatically.
                  </p>
                </div>
                <div className="text-sm font-medium text-text sm:text-right">
                  {sessionMediaUploadPercent}%
                </div>
              </div>

              <div className="mt-4 h-2 overflow-hidden rounded-full bg-panel2">
                <div
                  className="h-full rounded-full bg-text transition-all"
                  style={{ width: `${sessionMediaUploadPercent}%` }}
                />
              </div>

              <div className="mt-3 flex flex-col gap-2 text-xs text-text-faint sm:flex-row sm:items-center sm:justify-between">
                <span>
                  {formatFileSize(sessionMediaUploadedBytes) || '0 KB'} of {formatFileSize(sessionMediaProgressTotalBytes) || '0 KB'} uploaded
                </span>
                <span>
                  {sessionMediaTransferRate
                    ? `${sessionMediaTransferRate}${sessionMediaEta ? ` · about ${sessionMediaEta} remaining` : ''}`
                    : sessionMediaUploading ? 'Estimating time remaining...' : null}
                </span>
              </div>

              <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-text-faint">
                <span>{sessionMediaUploadedPhotoCount} of {sessionMediaUploadPhotoCount} photos uploaded</span>
                <span>{sessionMediaUploadedVideoCount} of {sessionMediaUploadVideoCount} videos uploaded</span>
              </div>

              {sessionMediaUploadError && (
                <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <p className="text-sm text-warn">{sessionMediaUploadError}</p>
                  <button
                    type="button"
                    onClick={() => uploadPendingSessionMedia()}
                    className="btn btn-sm self-start sm:self-auto"
                    disabled={sessionMediaUploading}
                  >
                    Retry upload
                  </button>
                </div>
              )}
            </div>
          </section>
        )}

        <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_420px] gap-7 items-start">
          <div className="min-w-0 w-full">
            <div className="mb-5 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex flex-wrap items-center gap-3">
                <div className="inline-flex rounded border border-line bg-panel p-1">
                  <button
                    type="button"
                    onClick={() => setActiveTab('settings')}
                    className={`h-9 px-4 rounded text-sm font-medium transition-colors ${
                      activeTab === 'settings' ? 'bg-text text-white' : 'text-text-dim hover:text-text'
                    }`}
                  >
                    Details
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveTab('recording')}
                    className={`h-9 px-4 rounded text-sm font-medium transition-colors ${
                      activeTab === 'recording' ? 'bg-text text-white' : 'text-text-dim hover:text-text'
                    }`}
                  >
                    Video
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveTab('photos')}
                    className={`h-9 px-4 rounded text-sm font-medium transition-colors ${
                      activeTab === 'photos' ? 'bg-text text-white' : 'text-text-dim hover:text-text'
                    }`}
                  >
                    Photos
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveTab('access')}
                    className={`h-9 px-4 rounded text-sm font-medium transition-colors ${
                      activeTab === 'access' ? 'bg-text text-white' : 'text-text-dim hover:text-text'
                    }`}
                  >
                    Publish
                  </button>
                </div>

                <label className={`h-11 inline-flex items-center justify-center rounded border border-line bg-panel px-4 text-sm font-medium transition-colors ${
                  uploadingPhotos || uploadingVideos
                    ? 'opacity-60 pointer-events-none'
                    : 'cursor-pointer text-text-dim hover:border-text/40 hover:text-text'
                }`}>
                  {uploadingPhotos || uploadingVideos ? 'Uploading...' : 'Upload media'}
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/avif,video/mp4,video/webm,video/quicktime,.mov"
                    multiple
                    disabled={uploadingPhotos || uploadingVideos}
                    onChange={uploadProductMedia}
                    className="hidden"
                  />
                </label>
              </div>

              <div className="flex flex-wrap items-center gap-2 lg:justify-end">
                <button
                  type="button"
                  onClick={cancelProductChanges}
                  disabled={!hasProductChanges || saving || deletingProduct || sessionMediaUploading}
                  className="btn disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => saveProduct()}
                  disabled={saving || deletingProduct || sessionMediaUploading || !canSaveChanges}
                  className="btn btn-accent disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? 'Saving...' : sessionMediaUploading ? 'Uploading...' : 'Save Changes'}
                </button>
              </div>
            </div>

            <section className="w-full">
            {activeTab === 'settings' ? (
              <ProductSettingsPanel
                title={title}
	                description={description}
		                attendeePrice={attendeePrice}
		                publicPrice={publicPrice}
		                coverImageUrl={coverImagePreviewUrl || coverImageUrl}
		                onTitleChange={setTitle}
		                onDescriptionChange={setDescription}
		                onAttendeePriceChange={setAttendeePrice}
		                onPublicPriceChange={setPublicPrice}
		                onCoverImageChange={handleCoverImageChange}
	                onRemoveCoverImage={removeCoverImage}
	              />
            ) : activeTab === 'access' ? (
              <ProductAccessPanel
                productVisibility={productVisibility}
                showOnProfile={showOnProfile}
                license={license}
                accessDurationCount={accessDurationCount}
                accessDurationUnit={accessDurationUnit}
                requiresBuyerApproval={requiresBuyerApproval}
                status={status}
                saving={saving}
                canSaveProduct={canSaveProduct}
                onProductVisibilityChange={setProductVisibility}
                onShowOnProfileChange={setShowOnProfile}
                onLicenseChange={setLicense}
                onAccessDurationCountChange={setAccessDurationCount}
                onAccessDurationUnitChange={setAccessDurationUnit}
                onRequiresBuyerApprovalChange={setRequiresBuyerApproval}
                onTogglePublish={() => {
                  if (status === 'ready') {
                    void setPublishState('unpublish');
                  } else if (status === 'unpublished') {
                    void setPublishState('republish');
                  } else if (status === 'draft') {
                    void saveProduct('ready');
                  }
                }}
              />
            ) : activeTab === 'recording' ? (
              <div className="space-y-3">
                {data.recordings.length === 0 ? (
                  <div className="bg-panel border border-line rounded-xl p-10 text-center">
                    <h2 className="font-display text-xl font-medium text-text mb-2">No recording ready yet</h2>
                    <p className="text-text-dim">Completed video recordings from this event will appear here for review, download, and sale.</p>
                  </div>
                ) : (
                  <>
                    {uploadedRecordings.length > 0 && (
                      <div className="space-y-3">
                        {uploadedRecordings.map(renderRecordingCard)}
                      </div>
                    )}
                    {backupRecordings.length > 0 && (
                      <section className="space-y-3 border-t border-line pt-4" aria-labelledby="backup-recordings-heading">
                        <div>
                          <h2 id="backup-recordings-heading" className="text-sm font-semibold text-text">
                            Backup recordings
                          </h2>
                          <p className="text-sm text-text-dim">
                            These recordings may be lower quality than model uploads
                          </p>
                        </div>
                        {backupRecordings.map(renderRecordingCard)}
                      </section>
                    )}
                  </>
                )}
              </div>
	            ) : (
	              <div className="space-y-4">
                  {activeCaptures.length === 0 ? (
                    <div className="bg-panel border border-line rounded-xl p-10 text-center">
                      <h2 className="font-display text-xl font-medium text-text mb-2">No captures ready yet</h2>
                      <p className="text-text-dim">Captured stills from this event will appear here after the model uploads them during breaks or after the session.</p>
                    </div>
                  ) : (
                    <>
	                <div className="flex flex-wrap gap-2">
	                  <button
	                    type="button"
	                    onClick={() => setActivePoseKey('all')}
	                    className={`h-9 px-3 rounded border text-sm transition-colors ${
	                      activePoseKey === 'all'
	                        ? 'border-text bg-text text-white'
	                        : 'border-line bg-panel text-text-dim hover:text-text'
	                    }`}
	                  >
	                    All photos · {activeCaptures.length}
	                  </button>
	                  {poseGroups.map((group) => (
	                    <button
	                      key={group.key}
	                      type="button"
	                      onClick={() => setActivePoseKey(group.key)}
	                      className={`h-9 px-3 rounded border text-sm transition-colors ${
	                        activePoseKey === group.key
	                          ? 'border-text bg-text text-white'
	                          : 'border-line bg-panel text-text-dim hover:text-text'
	                      }`}
	                    >
	                      {group.label} · {group.captures.length}
	                    </button>
	                  ))}
	                </div>

                    {cameraGroups.length > 1 && (
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={() => setActiveCameraKey('all')}
                          className={`h-9 px-3 rounded border text-sm transition-colors ${
                            activeCameraKey === 'all'
                              ? 'border-text bg-text text-white'
                              : 'border-line bg-panel text-text-dim hover:text-text'
                          }`}
                        >
                          All cameras · {poseFilteredCaptures.length}
                        </button>
                        {cameraGroups.map((group) => (
                          <button
                            key={group.key}
                            type="button"
                            onClick={() => setActiveCameraKey(group.key)}
                            className={`h-9 px-3 rounded border text-sm transition-colors ${
                              activeCameraKey === group.key
                                ? 'border-text bg-text text-white'
                                : 'border-line bg-panel text-text-dim hover:text-text'
                            }`}
                          >
                            {group.label} · {group.captures.length}
                          </button>
                        ))}
                      </div>
                    )}

                    {visibleCaptures.length === 0 ? (
                      <div className="bg-panel border border-line rounded-xl p-8 text-center">
                        <h2 className="font-display text-lg font-medium text-text mb-2">No photos match these filters</h2>
                        <p className="text-text-dim">Choose another pose or camera to continue selecting photos.</p>
                      </div>
                    ) : (
	                <div className="grid grid-cols-2 md:grid-cols-3 2xl:grid-cols-4 gap-3">
	                  {visibleCaptures.map((capture) => {
	                    const selected = selectedIds.has(capture.id);
                      const poseLabel = getCapturePoseLabel(capture);
                      const cameraLabel = cameraLabelByCaptureId.get(capture.id) ?? getCaptureCameraLabel(capture);
	                    return (
	                      <article
	                        key={capture.id}
	                        className={`group text-left bg-panel border rounded-lg overflow-hidden transition-colors ${
	                          selected ? 'border-text ring-2 ring-text/15' : 'border-line hover:border-text-dim'
	                        }`}
	                      >
	                        <button
	                          type="button"
	                          onClick={() => setLightboxCaptureId(capture.id)}
	                          className="relative block w-full bg-black aspect-[4/3]"
	                          aria-label="Open photo preview"
	                        >
	                          <img
	                            src={getReferencePackThumbnailUrl(capture)}
	                            alt={capture.poseName || 'Model reference capture'}
	                            className="w-full h-full object-contain"
	                            loading="lazy"
	                          />
	                        </button>
	                        <div className="p-3 bg-white">
	                          <div className="flex flex-col gap-3">
	                            <div className="min-w-0">
	                              <div className="text-[12px] text-text-faint">
	                                {capture.width && capture.height ? `${capture.width} x ${capture.height}` : 'Unknown size'}
	                                {capture.fileSizeBytes ? ` · ${formatFileSize(capture.fileSizeBytes)}` : ''}
	                              </div>
                                  <div className="mt-0.5 text-[12px] text-text-faint">
                                    {cameraLabel}
                                  </div>
	                            </div>
                                <div className="grid grid-cols-2 gap-2">
                                  <button
                                    type="button"
	                                    onClick={() => {
                                      void downloadCapture(capture, { poseLabel, cameraLabel });
                                    }}
                                    disabled={downloadingCaptureId === capture.id}
	                                    aria-label={`Download ${poseLabel} photo`}
	                                    className="h-8 rounded border border-line bg-white px-2 text-[12px] font-medium inline-flex items-center justify-center transition-colors hover:border-text hover:bg-bg-secondary disabled:cursor-not-allowed disabled:opacity-50"
	                                  >
	                                    {downloadingCaptureId === capture.id ? 'Preparing' : 'Download'}
	                                  </button>
		                            <button
		                              type="button"
		                              onClick={() => toggleSelected(capture)}
		                              aria-pressed={selected}
		                              aria-label={selected ? 'Remove from set' : 'Add'}
		                              className={`h-8 rounded border px-2 text-[12px] font-medium inline-flex items-center justify-center transition-colors ${
		                                selected
		                                  ? 'border-text bg-text text-white hover:bg-text/90'
		                                  : 'border-line bg-white text-text hover:border-text hover:bg-bg-secondary'
		                              }`}
		                            >
		                              {selected ? 'Added' : 'Add'}
		                            </button>
                                </div>
	                          </div>
	                        </div>
	                      </article>
	                    );
	                  })}
	                </div>
                    )}
                    </>
                  )}
	              </div>
	            )}
            </section>
          </div>

          <aside className="xl:sticky xl:top-20">
            <h2 className="section-title mb-4">Preview</h2>
            <ProductPreviewPanel
              title={title}
              description={description}
              publicPrice={publicPrice}
              attendeePrice={attendeePrice}
              productVisibility={productVisibility}
              license={license}
              accessDurationCount={accessDurationCount}
              accessDurationUnit={accessDurationUnit}
              status={status}
              requiresBuyerApproval={requiresBuyerApproval}
              coverImageUrl={coverImagePreviewUrl || coverImageUrl}
              selectedCaptures={selectedCaptures}
              selectedRecordings={selectedRecordings}
            />
            <ShareReferencePackLink
              url={shareableReferencePackUrl}
              isPublished={status === 'ready'}
              hasUnsavedChanges={hasProductChanges}
              copied={shareLinkCopied}
              onCopy={copyShareableLink}
            />
            {editingProductId && (
              <PackBuyersPanel productId={editingProductId} getToken={getToken} />
            )}
            {editingProductId && (
              <button
                type="button"
                onClick={deleteProduct}
                disabled={saving || deletingProduct || sessionMediaUploading}
                className="mt-4 w-full rounded-lg border border-warn/45 bg-warn/10 px-4 py-3 text-sm font-medium text-warn transition-colors hover:bg-warn/15 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {deletingProduct ? 'Deleting...' : 'Delete pack'}
              </button>
            )}
	          </aside>
        </div>
	      </main>

      <SharedFooter className="mt-auto" />

	      {lightboxCapture && (
	        <div
	          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
	          role="dialog"
	          aria-modal="true"
	          aria-label="Photo preview"
	          onClick={() => setLightboxCaptureId(null)}
	        >
	          <button
	            type="button"
	            onClick={() => setLightboxCaptureId(null)}
	            className="absolute top-4 right-4 h-10 w-10 rounded-full bg-white/10 text-white inline-flex items-center justify-center hover:bg-white/20"
	            aria-label="Close photo preview"
	          >
	            <span className="relative h-4 w-4" aria-hidden="true">
	              <span className="absolute left-1/2 top-0 h-full w-0.5 -translate-x-1/2 rotate-45 rounded-full bg-current" />
	              <span className="absolute left-1/2 top-0 h-full w-0.5 -translate-x-1/2 -rotate-45 rounded-full bg-current" />
	            </span>
	          </button>
	          <div className="max-w-[min(96vw,1400px)] max-h-[92vh]" onClick={(event) => event.stopPropagation()}>
	            <img
	              src={lightboxCapture.imageUrl}
	              alt={lightboxCapture.poseName || 'Model reference capture'}
	              className="max-w-full max-h-[84vh] object-contain mx-auto"
	            />
	            <div className="mt-3 flex flex-col gap-3 text-white text-sm sm:flex-row sm:items-center sm:justify-between">
                <button
	                  type="button"
	                  onClick={(event) => {
	                    event.stopPropagation();
                      void downloadCapture(lightboxCapture, {
                        poseLabel: lightboxCapturePoseLabel ?? getCapturePoseLabel(lightboxCapture),
                        cameraLabel: lightboxCaptureCameraLabel ?? getCaptureCameraLabel(lightboxCapture),
                      });
	                  }}
                    disabled={downloadingCaptureId === lightboxCapture.id}
	                  aria-label={`Download ${lightboxCapturePoseLabel ?? getCapturePoseLabel(lightboxCapture)} photo`}
	                  className="h-9 rounded border border-white/25 bg-white/10 px-4 text-sm font-medium text-white inline-flex items-center justify-center transition-colors hover:bg-white/20 disabled:cursor-not-allowed disabled:opacity-50"
	                >
	                  {downloadingCaptureId === lightboxCapture.id ? 'Preparing...' : 'Download photo'}
	                </button>
	              <span className="text-white/70 shrink-0">
	                {lightboxCapture.width && lightboxCapture.height ? `${lightboxCapture.width} x ${lightboxCapture.height}` : 'Unknown size'}
	                {lightboxCapture.fileSizeBytes ? ` · ${formatFileSize(lightboxCapture.fileSizeBytes)}` : ''}
	                {` · ${lightboxCaptureCameraLabel ?? getCaptureCameraLabel(lightboxCapture)}`}
	              </span>
	            </div>
	          </div>
	        </div>
	      )}
	    </div>
	  );
}

function CoverPhotoField({
  previewUrl,
  onChange,
  onRemove,
}: {
  previewUrl: string | null;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onRemove: () => void;
}) {
  return (
    <div className="mb-4">
      <div className="flex items-center justify-between gap-3 mb-2">
        <span className="text-sm text-text-dim">Cover photo</span>
        {previewUrl && (
          <button
            type="button"
            onClick={onRemove}
            className="text-[12px] text-text-dim border-b border-line pb-px hover:text-text hover:border-text"
          >
            Remove
          </button>
        )}
      </div>

      <label className="block cursor-pointer">
        <input type="file" accept="image/*" onChange={onChange} className="hidden" />
        {previewUrl ? (
          <div className="relative aspect-[4/3] overflow-hidden rounded-lg bg-black group">
            <img src={previewUrl} alt="Product cover preview" className="h-full w-full object-cover" />
            <div className="absolute inset-x-0 bottom-0 bg-black/55 px-3 py-2 text-[12px] text-white opacity-0 transition-opacity group-hover:opacity-100">
              Click to replace
            </div>
          </div>
        ) : (
          <div className="aspect-[4/3] rounded-lg border border-dashed border-line bg-bg-secondary flex items-center justify-center px-4 text-center text-[13px] text-text-dim hover:border-text/40 hover:text-text transition-colors">
            Upload a cover photo
          </div>
        )}
      </label>
    </div>
  );
}

function ProductSettingsPanel({
  title,
  description,
  attendeePrice,
  publicPrice,
  coverImageUrl,
  onTitleChange,
  onDescriptionChange,
  onAttendeePriceChange,
  onPublicPriceChange,
  onCoverImageChange,
  onRemoveCoverImage,
}: {
  title: string;
  description: string;
  attendeePrice: string;
  publicPrice: string;
  coverImageUrl: string | null;
  onTitleChange: (value: string) => void;
  onDescriptionChange: (value: string) => void;
  onAttendeePriceChange: (value: string) => void;
  onPublicPriceChange: (value: string) => void;
  onCoverImageChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onRemoveCoverImage: () => void;
}) {
  return (
    <div className="w-full space-y-5">
      <div className="w-full bg-panel border border-line rounded-xl p-5 shadow-sm">
        <div className="grid grid-cols-1 lg:grid-cols-[320px_minmax(0,1fr)] gap-5 items-start">
          <CoverPhotoField
            previewUrl={coverImageUrl}
            onChange={onCoverImageChange}
            onRemove={onRemoveCoverImage}
          />

          <div className="space-y-4">
            <label className="block">
              <span className="text-sm text-text-dim">Title</span>
              <input value={title} onChange={(e) => onTitleChange(e.target.value)} className="input mt-1 h-11 text-sm" />
            </label>

            <label className="block">
              <span className="text-sm text-text-dim">Description</span>
              <textarea
                value={description}
                onChange={(e) => onDescriptionChange(e.target.value)}
                className="input mt-1 min-h-[96px] h-auto py-3 text-sm resize-y"
                placeholder="A short note about the reference pack."
              />
            </label>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <label className="block">
                <span className="text-sm text-text-dim">Attendee price</span>
                <span className="block text-[11px] text-text-faint">Ticket holders</span>
                <div className="flex items-center mt-1">
                  <span className="h-11 px-3 inline-flex items-center border border-r-0 border-line rounded-l bg-bg-secondary text-text-dim">$</span>
                  <input value={attendeePrice} onChange={(e) => onAttendeePriceChange(e.target.value)} className="input h-11 rounded-l-none text-sm" inputMode="decimal" />
                </div>
              </label>

              <label className="block">
                <span className="text-sm text-text-dim">Public price</span>
                <span className="block text-[11px] text-text-faint">Non-attendees</span>
                <div className="flex items-center mt-1">
                  <span className="h-11 px-3 inline-flex items-center border border-r-0 border-line rounded-l bg-bg-secondary text-text-dim">$</span>
                  <input value={publicPrice} onChange={(e) => onPublicPriceChange(e.target.value)} className="input h-11 rounded-l-none text-sm" inputMode="decimal" />
                </div>
              </label>
            </div>
          </div>
        </div>

      </div>

    </div>
  );
}

function ProductAccessPanel({
  productVisibility,
  showOnProfile,
  license,
  accessDurationCount,
  accessDurationUnit,
  requiresBuyerApproval,
  status,
  saving,
  canSaveProduct,
  onProductVisibilityChange,
  onShowOnProfileChange,
  onLicenseChange,
  onAccessDurationCountChange,
  onAccessDurationUnitChange,
  onRequiresBuyerApprovalChange,
  onTogglePublish,
}: {
  productVisibility: ProductVisibility;
  showOnProfile: boolean;
  license: string;
  accessDurationCount: string;
  accessDurationUnit: AccessDurationUnit;
  requiresBuyerApproval: boolean;
  status: ProductStatus;
  saving: boolean;
  canSaveProduct: boolean;
  onProductVisibilityChange: (value: ProductVisibility) => void;
  onShowOnProfileChange: (value: boolean) => void;
  onLicenseChange: (value: string) => void;
  onAccessDurationCountChange: (value: string) => void;
  onAccessDurationUnitChange: (value: AccessDurationUnit) => void;
  onRequiresBuyerApprovalChange: (value: boolean) => void;
  onTogglePublish: () => void;
}) {
  const { t } = useCopy();

  return (
    <div className="w-full bg-panel border border-line rounded-xl p-5 shadow-sm">
      <div className="mb-5">
        <span className="text-sm text-text-dim">{t('referencePacks.editor.whoCanBuy')}</span>
        <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <button
            type="button"
            onClick={() => {
              onProductVisibilityChange('attendees_only');
              onShowOnProfileChange(false);
            }}
            aria-pressed={productVisibility === 'attendees_only'}
            className={`rounded-lg border p-3 text-left transition-colors ${
              productVisibility === 'attendees_only'
                ? 'border-text bg-bg-secondary text-text shadow-sm ring-1 ring-text/10'
                : 'border-line bg-bg-secondary text-text hover:border-text/40'
            }`}
          >
            <span className="block text-sm font-medium">{t('referencePacks.editor.attendeesOnly')}</span>
          </button>
          <button
            type="button"
            onClick={() => onProductVisibilityChange('anyone')}
            aria-pressed={productVisibility === 'anyone'}
            className={`rounded-lg border p-3 text-left transition-colors ${
              productVisibility === 'anyone'
                ? 'border-text bg-bg-secondary text-text shadow-sm ring-1 ring-text/10'
                : 'border-line bg-bg-secondary text-text hover:border-text/40'
            }`}
          >
            <span className="block text-sm font-medium">{t('referencePacks.editor.anyone')}</span>
          </button>
        </div>
        {productVisibility === 'anyone' && (
          <label className="mt-3 flex items-center gap-3 rounded-lg border border-line-soft bg-bg-secondary p-3 text-sm font-medium text-text">
            <input
              type="checkbox"
              checked={showOnProfile}
              onChange={(e) => onShowOnProfileChange(e.target.checked)}
            />
            <span>{t('referencePacks.editor.showOnProfile')}</span>
          </label>
        )}
      </div>

      <label className="mb-5 flex items-start gap-3 rounded-lg border border-line-soft bg-bg-secondary p-3">
        <input
          type="checkbox"
          checked={requiresBuyerApproval}
          onChange={(e) => onRequiresBuyerApprovalChange(e.target.checked)}
          className="mt-1"
        />
        <span>
          <span className="block text-sm font-medium text-text">{t('referencePacks.editor.requireApprovalTitle')}</span>
          <span className="block text-xs text-text-dim mt-1">
            {t('referencePacks.editor.requireApprovalHelp')}
          </span>
        </span>
      </label>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <label className="block">
          <span className="text-sm text-text-dim">Access length</span>
          <div className="mt-1 grid grid-cols-[minmax(0,1fr)_140px] gap-2">
            <input
              type="number"
              min="1"
              max="120"
              step="1"
              value={accessDurationCount}
              onChange={(event) => onAccessDurationCountChange(event.target.value.replace(/[^\d]/g, ''))}
              className="input h-11 text-sm"
              aria-label="Access length number"
            />
            <select
              value={accessDurationUnit}
              onChange={(event) => onAccessDurationUnitChange(event.target.value as AccessDurationUnit)}
              className="input h-11 text-sm"
              aria-label="Access length unit"
            >
              <option value="days">Days</option>
              <option value="weeks">Weeks</option>
              <option value="months">Months</option>
              <option value="years">Years</option>
            </select>
          </div>
          <p className="mt-1 text-[11px] leading-snug text-text-faint">
            Starts from the buyer's purchase date.
          </p>
          {(!Number.isInteger(Number(accessDurationCount)) || Number(accessDurationCount) <= 0) && (
            <p className="mt-1 text-[11px] text-warn">
              Enter a whole number greater than 0.
            </p>
          )}
        </label>

        <label className="block">
          <span className="text-sm text-text-dim">License</span>
          <select value={license} onChange={(e) => onLicenseChange(e.target.value)} className="input mt-1 h-11 text-sm">
            <option value="personal_reference">{getLicenseLabel('personal_reference')}</option>
            <option value="classroom_reference">{getLicenseLabel('classroom_reference')}</option>
          </select>
          <Link to="/reference-pack-licenses" className="mt-2 inline-block text-[11px] text-text-faint border-b border-line pb-px hover:text-text hover:border-text">
            Read the reference pack license terms
          </Link>
        </label>
      </div>

      <div className="mt-5 border-t border-line-soft pt-5">
        <button
          type="button"
          onClick={onTogglePublish}
          disabled={saving || !canSaveProduct || status === 'withdrawn'}
          className="btn btn-accent w-full disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving
            ? 'Saving...'
            : status === 'ready'
              ? 'Unpublish'
              : status === 'unpublished'
                ? 'Republish'
                : status === 'withdrawn'
                  ? 'Withdrawn by DrawClub'
                  : 'Publish'}
        </button>
        {status === 'unpublished' && (
          <p className="mt-2 text-[11px] leading-snug text-text-faint">
            Unpublished: new purchases are paused, existing buyers keep access, and you can keep editing.
          </p>
        )}
        {status === 'withdrawn' && (
          <p className="mt-2 text-[11px] leading-snug text-warn">
            This pack was withdrawn by DrawClub and is hidden from all viewers. Contact support to restore it.
          </p>
        )}
      </div>
    </div>
  );
}

interface PackBuyer {
  purchaseId: string;
  buyerProfileId: string;
  displayName: string | null;
  username: string | null;
  amount: number;
  currency: string;
  completedAt: string | null;
  accessExpiresOn: string | null;
  revokedAt: string | null;
  revocationReason: string | null;
}

function formatPurchaseDate(dateStr: string | null) {
  if (!dateStr) return null;
  return new Date(dateStr).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Buyers of this pack with seller self-serve per-buyer revocation. A reason is
 * mandatory; revoking never refunds; revoked access can be restored.
 */
function PackBuyersPanel({
  productId,
  getToken,
}: {
  productId: string;
  getToken: () => Promise<string | null>;
}) {
  const [buyers, setBuyers] = useState<PackBuyer[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<PackBuyer | null>(null);
  const [revokeReason, setRevokeReason] = useState('');
  const [busyPurchaseId, setBusyPurchaseId] = useState<string | null>(null);

  const loadBuyers = useCallback(async () => {
    try {
      setError(null);
      const token = await getToken();
      const response = await fetch(`/api/products/reference-packs/${productId}/buyers`, {
        headers: authHeaders(token),
      });

      if (!response.ok) {
        throw new Error('Failed to load buyers');
      }

      const result = await response.json();
      setBuyers(result.buyers ?? []);
    } catch (err) {
      console.error('Error loading reference pack buyers:', err);
      setError(err instanceof Error ? err.message : 'Failed to load buyers');
    }
  }, [productId, getToken]);

  useEffect(() => {
    void loadBuyers();
  }, [loadBuyers]);

  const confirmRevoke = async () => {
    if (!revokeTarget || revokeReason.trim().length === 0) return;

    setBusyPurchaseId(revokeTarget.purchaseId);
    try {
      const token = await getToken();
      const response = await fetch(
        `/api/products/reference-packs/${productId}/purchases/${revokeTarget.purchaseId}/revoke`,
        {
          method: 'POST',
          headers: jsonAuthHeaders(token),
          body: JSON.stringify({ reason: revokeReason.trim() }),
        }
      );

      if (!response.ok) {
        const result = await response.json().catch(() => null);
        const authMessage = signInMessageForAuthStatus(response, 'Please sign in again to revoke access');
        if (authMessage) throw new Error(authMessage);
        throw new Error(result?.error || 'Failed to revoke access');
      }

      setRevokeTarget(null);
      setRevokeReason('');
      await loadBuyers();
    } catch (err) {
      console.error('Error revoking reference pack access:', err);
      alert(err instanceof Error ? err.message : 'Failed to revoke access');
    } finally {
      setBusyPurchaseId(null);
    }
  };

  const restoreAccess = async (buyer: PackBuyer) => {
    setBusyPurchaseId(buyer.purchaseId);
    try {
      const token = await getToken();
      const response = await fetch(
        `/api/products/reference-packs/${productId}/purchases/${buyer.purchaseId}/restore`,
        {
          method: 'POST',
          headers: authHeaders(token),
        }
      );

      if (!response.ok) {
        const result = await response.json().catch(() => null);
        const authMessage = signInMessageForAuthStatus(response, 'Please sign in again to restore access');
        if (authMessage) throw new Error(authMessage);
        throw new Error(result?.error || 'Failed to restore access');
      }

      await loadBuyers();
    } catch (err) {
      console.error('Error restoring reference pack access:', err);
      alert(err instanceof Error ? err.message : 'Failed to restore access');
    } finally {
      setBusyPurchaseId(null);
    }
  };

  return (
    <section className="mt-4 rounded-lg border border-line bg-panel p-4 shadow-sm" data-testid="pack-buyers">
      <h3 className="text-sm font-medium text-text">Buyers</h3>

      {error ? (
        <p className="mt-2 text-xs text-warn">{error}</p>
      ) : buyers === null ? (
        <p className="mt-2 text-xs text-text-dim">Loading buyers...</p>
      ) : buyers.length === 0 ? (
        <p className="mt-2 text-xs text-text-dim">No completed purchases yet.</p>
      ) : (
        <ul className="mt-3 space-y-3">
          {buyers.map((buyer) => {
            const purchasedAt = formatPurchaseDate(buyer.completedAt);
            const buyerName = buyer.displayName || 'Buyer';
            const revoked = Boolean(buyer.revokedAt);

            return (
              <li
                key={buyer.purchaseId}
                className="rounded border border-line-soft bg-bg-secondary p-3"
                data-testid={`pack-buyer-${buyer.buyerProfileId}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-text">{buyerName}</p>
                    <p className="mt-0.5 text-[11px] text-text-dim">
                      {purchasedAt ? `Purchased ${purchasedAt}` : 'Purchase date unknown'}
                      {revoked ? ' · Access revoked' : ''}
                    </p>
                    {revoked && buyer.revocationReason && (
                      <p className="mt-1 text-[11px] leading-snug text-warn">
                        Reason: {buyer.revocationReason}
                      </p>
                    )}
                  </div>
                  {revoked ? (
                    <button
                      type="button"
                      onClick={() => restoreAccess(buyer)}
                      disabled={busyPurchaseId === buyer.purchaseId}
                      className="btn btn-sm shrink-0 disabled:cursor-not-allowed disabled:opacity-50"
                      data-testid="restore-access"
                    >
                      {busyPurchaseId === buyer.purchaseId ? 'Restoring...' : 'Restore access'}
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => {
                        setRevokeReason('');
                        setRevokeTarget(buyer);
                      }}
                      disabled={busyPurchaseId === buyer.purchaseId}
                      className="btn btn-sm shrink-0 border-warn/45 text-warn disabled:cursor-not-allowed disabled:opacity-50"
                      data-testid="revoke-access"
                    >
                      Revoke access
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {revokeTarget && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          role="dialog"
          aria-modal="true"
          aria-label="Revoke buyer access"
        >
          <div className="w-full max-w-md rounded-lg border border-line bg-panel p-5 shadow-lg">
            <h4 className="text-base font-medium text-text">
              Revoke access for {revokeTarget.displayName || 'this buyer'}?
            </h4>
            <p className="mt-2 text-sm leading-relaxed text-text-dim">
              They will no longer be able to open this reference pack. This does not refund their purchase, and you can restore access later.
            </p>
            <label className="mt-4 block">
              <span className="text-sm text-text-dim">Reason (required)</span>
              <textarea
                value={revokeReason}
                onChange={(event) => setRevokeReason(event.target.value)}
                className="input mt-1 min-h-[88px] h-auto py-3 text-sm resize-y"
                placeholder="Why are you revoking this buyer's access?"
                data-testid="revoke-reason"
              />
            </label>
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setRevokeTarget(null);
                  setRevokeReason('');
                }}
                className="btn"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void confirmRevoke()}
                disabled={revokeReason.trim().length === 0 || busyPurchaseId === revokeTarget.purchaseId}
                className="btn btn-accent disabled:cursor-not-allowed disabled:opacity-50"
                data-testid="confirm-revoke"
              >
                {busyPurchaseId === revokeTarget.purchaseId ? 'Revoking...' : 'Revoke access'}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

function ShareReferencePackLink({
  url,
  isPublished,
  hasUnsavedChanges,
  copied,
  onCopy,
}: {
  url: string | null;
  isPublished: boolean;
  hasUnsavedChanges: boolean;
  copied: boolean;
  onCopy: () => void;
}) {
  return (
    <section className="mt-4 rounded-lg border border-line bg-panel p-4 shadow-sm">
      <h3 className="text-sm font-medium text-text">Share link</h3>
      <p className="mt-1 text-xs leading-relaxed text-text-dim">
        {url
          ? isPublished
            ? 'Copy this link for people to view and buy the reference pack.'
            : 'Publish this pack before sharing. Draft packs are not visible to buyers.'
          : 'Save this pack to create a shareable link.'}
      </p>

      {url && (
        <>
          <div className="mt-3 flex flex-col gap-2 sm:flex-row xl:flex-col 2xl:flex-row">
            <input
              readOnly
              value={url}
              aria-label="Reference pack shareable link"
              onFocus={(event) => event.currentTarget.select()}
              className="input h-10 min-w-0 flex-1 font-mono text-[12px]"
            />
            <button
              type="button"
              onClick={onCopy}
              className="btn btn-sm shrink-0"
            >
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>

          {hasUnsavedChanges && (
            <p className="mt-2 text-[11px] leading-snug text-text-faint">
              Save changes before sharing the latest version.
            </p>
          )}
        </>
      )}
    </section>
  );
}

function ProductPreviewPanel({
  title,
  description,
  publicPrice,
  attendeePrice,
  productVisibility,
  license,
  accessDurationCount,
  accessDurationUnit,
  status,
  coverImageUrl,
  selectedCaptures,
  selectedRecordings,
}: {
  title: string;
  description: string;
  publicPrice: string;
  attendeePrice: string;
  productVisibility: ProductVisibility;
  license: string;
  accessDurationCount: string;
  accessDurationUnit: AccessDurationUnit;
  status: ProductStatus;
  requiresBuyerApproval: boolean;
  coverImageUrl: string | null;
  selectedCaptures: ReferencePackAsset[];
  selectedRecordings: ModelRecording[];
}) {
  const attendeesOnly = productVisibility === 'attendees_only';
  const rawPrice = attendeesOnly ? attendeePrice : publicPrice;
  const priceValue = Number(rawPrice) || 0;
  const priceLabel = formatPrice(priceValue);
  const coverPreviewUrl = coverImageUrl || (selectedCaptures[0] ? getReferencePackThumbnailUrl(selectedCaptures[0]) : null);
  const accessDurationLabel = getAccessDurationLabel(accessDurationCount, accessDurationUnit);
  const isPublished = status === 'ready';
  const statusLabel = status === 'ready'
    ? 'Published'
    : status === 'unpublished'
      ? 'Unpublished'
      : status === 'withdrawn'
        ? 'Withdrawn'
        : 'Draft';

  return (
    <div className="relative">
      <span
        className={`pointer-events-none absolute left-3 top-3 z-10 inline-flex rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-wide shadow-sm ${
          isPublished
            ? 'bg-accent text-white'
            : status === 'withdrawn'
              ? 'bg-warn text-white'
              : 'border border-line bg-white/95 text-text-dim'
        }`}
      >
        {statusLabel}
      </span>
      <ReferencePackCard
        priceLabelOverride={priceLabel}
        pack={{
          title: title || 'Untitled product',
          description,
          priceCents: priceValue * 100,
          currency: 'USD',
          coverImageUrl: coverPreviewUrl,
          photoCount: selectedCaptures.length,
          videoCount: selectedRecordings.length,
          license,
          accessDurationLabel,
          accessExpiresOn: null,
        }}
      />
    </div>
  );
}
